import { useState } from "react";
import { useNavigate } from "react-router";

import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import { useCreateSession } from "@vega-api/generated/session-v1/session-v1";
import type { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { getProfile } from "@vega-api/generated/user-management-v1/user-management-v1";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import { m } from "@vega-core/paraglide/messages";

import { CommonPaths } from "../../paths";

type LocalSessionFormProps = {
  portal: FrontendPortal;
};

export const LocalSessionForm = ({ portal }: LocalSessionFormProps) => {
  const apiKey = import.meta.env.VITE_LOCAL_SESSION_API_KEY;
  const session = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = useCreateSession({
    mutation: {
      onSuccess: async () => {
        const { data } = await getProfile();

        if (data) {
          session.setUserData(data);
        }

        navigate(CommonPaths.Home);
      },
      onError: (mutationError) => {
        const responseMessage = mutationError.response?.data?.message;
        setError(responseMessage ?? m.localLogin_unexpectedError());
      },
    },
    request: {
      headers: { "x-api-key": apiKey ?? "" },
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (!apiKey) {
      setError(m.localLogin_missingApiKey());

      return;
    }
    mutate({ data: { email, portal } });
  };

  return (
    <form onSubmit={handleSubmit}>
      <p className="body-small-regular text-text/secondary mb-4">
        {m.localLogin_formDescription()}
      </p>
      <div className="mb-4">
        <InputControl
          label={m.formFields_email()}
          name="local-session-email"
          onChange={(event) => setEmail(event.target.value)}
          placeholder="alice@vega.investments"
          type="email"
          value={email}
          withBorder
        />
      </div>
      {error ? (
        <div className="mb-3">
          <ValidationErrorText dataTestId="local-session">
            {error}
          </ValidationErrorText>
        </div>
      ) : null}
      <ThemedButton
        dataTestId="local-session-submit-button"
        disabled={isPending || !email}
        fullWidth
        type="submit"
        variant="primary"
      >
        {isPending ? m.localLogin_signingIn() : m.localLogin_signIn()}
      </ThemedButton>
    </form>
  );
};
